'use client'

/**
 * Read a local image's intrinsic size in the browser without uploading it.
 * Decodes through an `<img>` (first frame for animated formats), so it is
 * cheap even for a 20MB GIF, and resolves `null` for anything the browser
 * cannot decode instead of throwing — callers treat dimensions as a hint.
 *
 * Bounded: a decoder that never fires `load` or `error` (a corrupt file, a
 * headless DOM) resolves `null` after {@link MEASURE_TIMEOUT_MS} rather than
 * holding the upload it was meant to decorate.
 */

const MEASURE_TIMEOUT_MS = 5000

export function measureImageFile(
  file: Blob
): Promise<{ width: number; height: number } | null> {
  if (
    typeof Image === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return Promise.resolve(null)
  }
  return new Promise((resolve) => {
    const previewUrl = URL.createObjectURL(file)
    const image = new Image()
    let isSettled = false
    const finish = (value: { width: number; height: number } | null): void => {
      if (isSettled) {
        return
      }
      isSettled = true
      clearTimeout(timer)
      URL.revokeObjectURL(previewUrl)
      resolve(value)
    }
    const timer = setTimeout(() => finish(null), MEASURE_TIMEOUT_MS)
    image.onload = () => {
      const width = image.naturalWidth
      const height = image.naturalHeight
      finish(width > 0 && height > 0 ? { width, height } : null)
    }
    image.onerror = () => finish(null)
    image.src = previewUrl
  })
}
